import { PRE_ASSESSMENT_STEP_PATHS } from '@/constants';

import BusinessConcept from '@/containers/BusinessConcept';
import ConfirmCopy from '@/containers/ConfirmCopy';
import FCANumber from '@/containers/FCANumber';
import MainSellingCountry from '@/containers/MainSellingCountry';
import PreAssessmentSmeCompanyDetails from '@/containers/PreAssessmentSmeCompanyDetails';
import PreAssessmentOverview from '@/containers/PreAssessmentOverview';
import PreAssessmentProducts from '@/containers/PreAssessmentProducts';
import PreAssessmentSubmitStep from '@/containers/PreAssessmentSubmitStep';
import Questionnaire from '@/containers/Questionnaire/Questionnaire';
import SellingCountries from '@/containers/SellingCountries';
import UnderwritingInfo from '@/containers/UnderwritingInfo';

import { isManagedMerchant } from '@/selectors/merchant';

import { hasHighExposure, shouldRenderPMLQuestionnaires } from '@/flow/configs/selectors';

import {
  canShowSummary,
  hasAtLeastOneBusinessConcept,
  hasAtLeastOneProduct,
  hasAtLeastOneSellingCountry,
  hasBusinessConcept,
  hasCategory,
  hasCompanyDetails,
  hasMainSellingCountry,
  hasOneSellingCountry,
  hasUnderwritingInfo,
  isSubmitted,
  needsRefreshOnQuestionnaires,
  shouldCollectFCANumber,
} from '../selectors';

import { getQuestionnaireFlow } from '../common/questionnaires';
import { getPredefinedCopySourceMid } from '@/flow/configs/utils/prefillOverwrite';

/**
 * Define the Pre-Assessment flow.
 */
export default ({ questionnaires: allQuestionnaires, merchant }) => {
  /**
   * Define sub-flow for high exposure questionnaires.
   */
  const questionnaireFlow = getQuestionnaireFlow({
    allQuestionnaires,
    back: [
      {
        condition: 'shouldRenderPMLQuestionnaires',
        path: PRE_ASSESSMENT_STEP_PATHS.HIGH_PML_QUESTIONNAIRE,
      },
      {
        condition: true,
        path: PRE_ASSESSMENT_STEP_PATHS.BUSINESS_CONCEPT_STEP,
      },
    ],
    next: PRE_ASSESSMENT_STEP_PATHS.SUMMARY_STEP,
  });

  const excludeCopyConfirmationStage = !getPredefinedCopySourceMid(merchant);

  return {
    globalPrerequisites: [
      {
        prerequisite: 'isManagedMerchant',
        back: PRE_ASSESSMENT_STEP_PATHS.SELLING_COUNTRIES_STEP,
      },
    ],
    selectors: {
      canShowSummary,
      hasAtLeastOneBusinessConcept,
      hasAtLeastOneProduct,
      hasAtLeastOneSellingCountry,
      hasBusinessConcept,
      hasCategory,
      hasMainSellingCountry,
      hasOneSellingCountry,
      hasUnderwritingInfo,
      hasCompanyDetails,
      hasHighExposure,
      isManagedMerchant,
      isSubmitted,
      needsRefreshOnQuestionnaires,
      shouldCollectFCANumber,
      shouldRenderPMLQuestionnaires,
    },
    neededCaps: {},
    steps: [
      {
        id: 'confirm-copy',
        path: PRE_ASSESSMENT_STEP_PATHS.CONFIRM_MERCHANT_COPY_STEP,
        Container: ConfirmCopy,
        back: false,
        exclude: excludeCopyConfirmationStage,
        next: PRE_ASSESSMENT_STEP_PATHS.SELLING_COUNTRIES_STEP,
        isFallback: !excludeCopyConfirmationStage,
      },
      {
        id: 'selling-countries',
        path: PRE_ASSESSMENT_STEP_PATHS.SELLING_COUNTRIES_STEP,
        Container: SellingCountries,
        back: false,
        next: PRE_ASSESSMENT_STEP_PATHS.MAIN_SELLING_COUNTRY_STEP,
        isFallback: excludeCopyConfirmationStage,
      },
      {
        id: 'main-selling-country',
        path: PRE_ASSESSMENT_STEP_PATHS.MAIN_SELLING_COUNTRY_STEP,
        prerequisite: 'hasAtLeastOneSellingCountry',
        Container: MainSellingCountry,
        skip: 'hasOneSellingCountry',
        back: PRE_ASSESSMENT_STEP_PATHS.SELLING_COUNTRIES_STEP,
        next: PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_PRODUCTS_STEP,
      },
      {
        id: 'pre-assessment-products',
        path: PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_PRODUCTS_STEP,
        prerequisite: 'hasMainSellingCountry',
        Container: PreAssessmentProducts,
        back: PRE_ASSESSMENT_STEP_PATHS.MAIN_SELLING_COUNTRY_STEP,
        next: PRE_ASSESSMENT_STEP_PATHS.UNDERWRITING_INFO_STEP,
      },
      {
        id: 'underwriting-info',
        path: PRE_ASSESSMENT_STEP_PATHS.UNDERWRITING_INFO_STEP,
        prerequisite: 'hasAtLeastOneProduct',
        Container: UnderwritingInfo,
        back: PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_PRODUCTS_STEP,
        next: PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_COMPANY_DETAILS_STEP,
      },
      {
        id: 'pre-assessment-company-details',
        path: PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_COMPANY_DETAILS_STEP,
        prerequisite: 'hasUnderwritingInfo',
        Container: PreAssessmentSmeCompanyDetails,
        back: PRE_ASSESSMENT_STEP_PATHS.UNDERWRITING_INFO_STEP,
        next: [
          {
            condition: 'shouldCollectFCANumber',
            path: PRE_ASSESSMENT_STEP_PATHS.FCA_NUMBER_STEP,
          },
          {
            condition: true,
            path: PRE_ASSESSMENT_STEP_PATHS.BUSINESS_CONCEPT_STEP,
          },
        ],
        isFallback: true,
      },
      {
        id: 'fca-number',
        path: PRE_ASSESSMENT_STEP_PATHS.FCA_NUMBER_STEP,
        Container: FCANumber,
        prerequisite: 'hasCompanyDetails',
        back: PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_COMPANY_DETAILS_STEP,
        next: PRE_ASSESSMENT_STEP_PATHS.BUSINESS_CONCEPT_STEP,
      },
      {
        id: 'business-concepts',
        path: PRE_ASSESSMENT_STEP_PATHS.BUSINESS_CONCEPT_STEP,
        Container: BusinessConcept,
        prerequisite: 'hasCompanyDetails',
        back: [
          {
            condition: 'shouldCollectFCANumber',
            path: PRE_ASSESSMENT_STEP_PATHS.FCA_NUMBER_STEP,
          },
          {
            condition: true,
            path: PRE_ASSESSMENT_STEP_PATHS.PRE_ASSESSMENT_COMPANY_DETAILS_STEP,
          },
        ],
        next: [
          {
            condition: 'shouldRenderPMLQuestionnaires',
            path: PRE_ASSESSMENT_STEP_PATHS.HIGH_PML_QUESTIONNAIRE,
          },
          {
            condition: true,
            path: questionnaireFlow.getFirst(),
          },
        ],
      },
      {
        id: 'high-pml-questionnaire',
        path: PRE_ASSESSMENT_STEP_PATHS.HIGH_PML_QUESTIONNAIRE,
        prerequisite: 'hasAtLeastOneProduct',
        Container: Questionnaire,
        back: PRE_ASSESSMENT_STEP_PATHS.BUSINESS_CONCEPT_STEP,
        next: questionnaireFlow.getFirst(),
        extraProps: { questionnaireIdentifier: 'HIGH_PML' },
      },
      ...questionnaireFlow.getSteps(),
      {
        id: 'pre-assessment-summary',
        path: PRE_ASSESSMENT_STEP_PATHS.SUMMARY_STEP,
        prerequisite: 'canShowSummary',
        Container: PreAssessmentSubmitStep,
        back: questionnaireFlow.getLast(),
        next: PRE_ASSESSMENT_STEP_PATHS.OVERVIEW_STEP,
      },
      {
        id: 'pre-assessment-overview',
        path: PRE_ASSESSMENT_STEP_PATHS.OVERVIEW_STEP,
        prerequisite: 'isSubmitted',
        Container: PreAssessmentOverview,
        back: PRE_ASSESSMENT_STEP_PATHS.SUMMARY_STEP,
        next: false,
      },
    ],
  };
};
